export type AuditUiCategory =
  | 'All Audits'
  | 'Pipeline'
  | 'Mapping'
  | 'Rules'
  | 'FTP'
  | 'Message'
  | 'Job'
  | 'Config / Agent'
  | 'Other';

export const AUDIT_CATEGORY_TABS: AuditUiCategory[] = [
  'All Audits',
  'Pipeline',
  'Mapping',
  'Rules',
  'FTP',
  'Message',
  'Job',
  'Config / Agent',
  'Other',
];

function toSearchString(value: unknown): string {
  if (value == null) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/** Builds lowercase searchable text from an audit record (use raw API values). */
export function buildAuditSearchText(record: Record<string, unknown>): string {
  return [
    record.UserName,
    record.ActionType,
    record.Description,
    record.context,
    record.contextid,
    toSearchString(record.OldValue),
    toSearchString(record.NewValue),
  ]
    .join(' ')
    .toLowerCase();
}

/**
 * Classifies a pipeline audit record into a UI category using Description and value text.
 * ActionType is often null in API responses, so Description/NewValue/OldValue drive matching.
 */
export function classifyAuditRecord(record: Record<string, unknown>): AuditUiCategory {
  const desc = (record.Description ?? '').toString().trim();
  const descLower = desc.toLowerCase();
  const newVal = toSearchString(record.NewValue).toLowerCase();
  const oldVal = toSearchString(record.OldValue).toLowerCase();
  const text = buildAuditSearchText(record);
  const ctx = (record.context ?? '').toString().toLowerCase();

  if (ctx === 'interface') {
    return 'Pipeline';
  }

  // FTP / credentials
  if (
    descLower.includes('ftp') ||
    descLower.includes('password') ||
    descLower.includes('credential') ||
    newVal.includes('ftp user') ||
    (descLower.includes('pipeline saved') && newVal.includes('ftp')) ||
    (!desc && (newVal.includes('"remotePath"') || newVal.includes('"connectivitytype"')))
  ) {
    return 'FTP';
  }

  // Message processing
  if (
    descLower.includes('message pushed') ||
    descLower.includes('reprocess') ||
    newVal.includes('transactionid') ||
    newVal.includes('messageid')
  ) {
    return 'Message';
  }

  // Mapping
  if (
    descLower.includes('mapping version') ||
    descLower.includes('mapped xml') ||
    descLower.includes('lookup ') ||
    (descLower === 'version status updated successfully' &&
      (newVal.includes('mapping') || newVal.includes('mapped xml')))
  ) {
    return 'Mapping';
  }

  // Rules
  if (
    descLower.includes('varule') ||
    descLower.includes('va rule') ||
    descLower.includes('rule not updated') ||
    descLower.includes('routing ruleconfig') ||
    descLower.includes('ruleconfig') ||
    (newVal.includes('rule update') && !newVal.includes('handler'))
  ) {
    return 'Rules';
  }

  // Job (before Pipeline — job activate/update also changes status : passive/active)
  if (
    ctx === 'job' ||
    descLower.includes('job activated') ||
    descLower.includes('job updated') ||
    descLower.includes('job created') ||
    descLower.includes('job deactivated') ||
    descLower.includes('vb job')
  ) {
    return 'Job';
  }

  // Config / Agent runtime
  if (
    descLower.includes('config updated') ||
    descLower.includes('config created') ||
    descLower.includes('agent config') ||
    descLower.includes('pipeline config') ||
    descLower.includes('product agent') ||
    newVal.includes('product agent') ||
    ctx === 'processor' ||
    ctx === 'agent' ||
    ctx === 'product agent'
  ) {
    return 'Config / Agent';
  }

  // Pipeline / interface lifecycle
  if (
    descLower.includes('pipeline') ||
    descLower.includes('interface') ||
    descLower.includes('handler') ||
    descLower.includes('additional settings') ||
    descLower.includes('activate handler') ||
    oldVal.includes('status :') ||
    oldVal.includes('mode :')
  ) {
    return 'Pipeline';
  }

  return 'Other';
}

/** Returns category tabs to display based on records present in the loaded audit list. */
export function getAvailableCategoryTabs(
  records: { uiCategory?: AuditUiCategory }[]
): AuditUiCategory[] {
  if (!records?.length) {
    return [];
  }

  const categoriesWithData = new Set<AuditUiCategory>();
  records.forEach(record => {
    if (record.uiCategory) {
      categoriesWithData.add(record.uiCategory);
    }
  });

  const populated = AUDIT_CATEGORY_TABS.filter(
    category => category !== 'All Audits' && categoriesWithData.has(category)
  );

  return ['All Audits', ...populated];
}

/** Default selected tab when the available tab set changes. */
export function getDefaultSelectedCategory(_tabs: AuditUiCategory[]): AuditUiCategory {
  return 'All Audits';
}
